import { useSyncExternalStore } from 'react';

const TICK_MS = 60_000;

let tick = 0;
const listeners = new Set();
let intervalId = null;
let timeoutId = null;
let visibilityAttached = false;

function emit() {
  tick += 1;
  listeners.forEach((fn) => fn());
}

function clearTimers() {
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function scheduleRepeating() {
  clearTimers();
  if (typeof document === 'undefined' || document.visibilityState === 'hidden') {
    return;
  }
  const delay = TICK_MS - (Date.now() % TICK_MS);
  timeoutId = window.setTimeout(() => {
    emit();
    intervalId = window.setInterval(emit, TICK_MS);
  }, delay);
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    clearTimers();
  } else {
    emit();
    scheduleRepeating();
  }
}

function attachVisibility() {
  if (visibilityAttached || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  visibilityAttached = true;
}

function detachVisibility() {
  if (!visibilityAttached) return;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  visibilityAttached = false;
}

function subscribe(onStoreChange) {
  listeners.add(onStoreChange);
  if (listeners.size === 1) {
    attachVisibility();
    scheduleRepeating();
  }
  return () => {
    listeners.delete(onStoreChange);
    if (listeners.size === 0) {
      clearTimers();
      detachVisibility();
    }
  };
}

function getSnapshot() {
  return tick;
}

function getServerSnapshot() {
  return 0;
}

/**
 * Subscribes to a shared clock that advances once per minute (aligned to wall-clock minutes)
 * while the document is visible. Used so relative date strings ("5m ago", "in 2h") stay fresh
 * without per-component timers or API calls. Pauses when the tab is hidden; refreshes once
 * when the tab becomes visible again.
 */
export function useRelativeTimeTick() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
